/**
 * Confirm dialog for a group of Risk settings changes: lists every change as
 * old → new, and collects the reason the server requires for the change log.
 */

import { useEffect, useRef, useState } from 'react';

import { REASON_MAX, REASON_MIN } from '@/services/riskSettingsApi';

export interface PendingChange {
  readonly id: string;
  readonly label: string;
  readonly oldText: string;
  readonly newText: string;
}

interface Props {
  readonly title: string;
  readonly changes: ReadonlyArray<PendingChange>;
  readonly takesEffect: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (reason: string) => void;
}

export function ConfirmChangesDialog({ title, changes, takesEffect, busy, onCancel, onConfirm }: Props): JSX.Element {
  const [reason, setReason] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const trimmed = reason.trim();
  const ok = trimmed.length >= REASON_MIN && trimmed.length <= REASON_MAX;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rs-confirm-title"
      onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}
    >
      <div className="modal rs-confirm">
        <h3 id="rs-confirm-title">{title}</h3>
        <ul className="rs-confirm__list">
          {changes.map((c) => (
            <li key={c.id}>
              <div style={{ fontWeight: 600 }}>{c.label}</div>
              <span className="rs-confirm__old">{c.oldText}</span>
              {' → '}
              <span className="rs-confirm__new">{c.newText}</span>
            </li>
          ))}
        </ul>
        <p className="form-hint" style={{ marginTop: 0 }}>{takesEffect}</p>
        <label className="form-field" htmlFor="rs-reason">
          <span className="form-field__label">Reason for the change (required)</span>
          <textarea
            id="rs-reason"
            ref={ref}
            className="rs-reason"
            maxLength={REASON_MAX}
            value={reason}
            disabled={busy}
            placeholder="For example: testing Moderate follow-up for the pilot review"
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="rs-option-hint">
          Saved to the change log with your name and the time. At least {REASON_MIN} characters.
        </div>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn--primary" disabled={busy || !ok} onClick={() => onConfirm(trimmed)}>
            {busy ? 'Saving…' : `Save ${changes.length} change${changes.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
